import { toRaw } from 'vue'

import ReportForm from './ReportForm.vue'
import ReportQuick from './ReportQuick.vue'
import ReportHedis from './ReportHedis.vue'
import ReportUnified from './ReportUnified.vue'

import lgAutoplay from 'lightgallery/plugins/autoplay';
import lgFullscreen from 'lightgallery/plugins/fullscreen';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from '@/assets/dependencies/lightGallery/plugins/zoom';
import { useUserStore } from '@/store/user'

const BASE_URL = process.env.VUE_APP_API_URL
const IS_PRODUCTION = process.env.NODE_ENV == 'production'

const DEFAULT_HEDIS_REPORT_DATA = {
  imageQuality: { OD: 'Yes', OS: 'Yes' },
  selectedDiabetesType: 'type_2',
  eyeConditions: { OD: '', OS: '' },
  selectedCptCodes: [],
  selectedProviderComments: [],
  selectedReferralOptions: []
};

export default {
  name: 'report',
  props: ['report_id', 'exam_id', 'patientReportInfo', 'unifiedExamType'],
  components: {
    ReportForm,
    ReportQuick,
    ReportHedis,
    ReportUnified
  },
  data: () => ({
    loading: false,
    alert: {},
    alertImages: {},
    templateOptions: [],
    columnOptions: [],

    selectedTemplate: null,

    examImages: [],
    patient: {},
    selectedImages: [],
    selectedImagesLoaded: [],
    dataPath: null,
    thumbDataPath: null,
    exam: {},

    quickReportEntries: [],
    loadedDiagnosisEntries: {
      right: [],
      left: []
    },
    diagnosisOptions: [],

    customTemplate: '',
    currentReport: {
      isQuickReport: true,
      isHedisReport: false,
      fullWidthPanoramic: false,
      observation: ''
    },
    hedisReportData: { ...DEFAULT_HEDIS_REPORT_DATA },
    lGalleryDiagnosisSettings: {
      showZoomInOutIcons: true,
      actualSize: false,
      speed: 300,
      licenseKey: 'CE440F6C-34CE4C2A-86065648-48895C83',
      plugins: [lgAutoplay, lgFullscreen, lgThumbnail, lgZoom],
    },
    lightGallery: null,
    isLoaded: false,
    showSelectedDiagnosisBox: false,
    tabActive: 'quickReport',
    quickReportObservation: '',
    completeReportObservation: '',
    hedisReportObservation: '',
    hasHedisReportFeature: false,
    DateOfScreening: '',
    dateOfBirth: "",
    selectedDiagnosis: [],
    hasUnifiedReportFeature: false,
    showExperienceAlert: true,
    isHedisUnifiedReport: false,
    canSwitchReportExperience: false,
    hasFormChanged: false,
    initialForm: null,
    unifiedReportData: null
  }),
  watch: {
    examImages(newVal, oldVal) {
      this.$nextTick(() => {
        // window.addEventListener("resize", this.getAndSetWidth);
        this.lightGallery.refresh();
      });
    },

    reportFormState: {
      handler(newValue) {
        if (this.initialForm !== null) {
          const rawNewValue = toRaw(newValue);
          this.hasFormChanged = !_.isEqual(rawNewValue, this.initialForm);
        }
      },
      deep: true,
    },
  },
  computed: {
    canSaveReport() {
      if (this.session?.isGlobalClient)
        return true

      const userId = this.session?.collaborator?.id
      const responsibleId = this.currentReport?.responsible?.id

      const permissions = String(this.session?.permissions)
      const isSuperUser = permissions.includes('admin') || permissions.includes('owner')

      if (isSuperUser)
        return true

      const isAuthor = userId != null && userId === responsibleId
      const isSignedByMedic = !!this.currentReport?.responsible?.medic
      const hasTechnicalProfile = permissions.includes('physician') || permissions.includes('technician') || permissions.includes('specialist')

      if (isSignedByMedic)
        return isAuthor

      return hasTechnicalProfile || isAuthor
    },
    reportFormState() {
      return {
        selectedTemplate: this.selectedTemplate,
        selectedImages: this.selectedImages,
        quickReportEntries: this.quickReportEntries,
        customTemplate: this.customTemplate,
        currentReport: this.currentReport,
        hedisReportData: this.hedisReportData,
        quickReportObservation: this.quickReportObservation,
        completeReportObservation: this.completeReportObservation,
        hedisReportObservation: this.hedisReportObservation,
        selectedDiagnosis: this.selectedDiagnosis,
        unifiedReportData: this.unifiedReportData,
      };
    }
  },
  async created() {
    this.templateOptions = [
      {id: null, name: this.$i18n.t('exam.selectTemplate'), content: ""},
    ]
    this.columnOptions = [
      {value: 1, label: this.$i18n.t('exam.oneColumn')},
      {value: 2, label: this.$i18n.t('exam.twoColumn')},
      {value: 3, label: this.$i18n.t('exam.threeColumn')},
      {value: 4, label: this.$i18n.t('exam.fourColumn')}
    ]
    // window.addEventListener("resize", this.getAndSetWidth);
  },
  async mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo

    await this.initializeReports();

    await this.fetchData();
    this.initializeView();
    this.isLoaded = true

    // $('#selectedDiagnosis').innerHTML = '' // avoid duplicated CID's

    this.$nextTick(() => {
      if (this.hasHedisReportFeature && this.selectedImagesLoaded.length == 0)
        this.checkImagesColorRL()

      if (this.selectedImagesLoaded.length > 0)
        this.loadImages()
      else if (this.hasUnifiedReportFeature)
        this.checkUnifiedDefaultImages()
      else if (this.hasHedisReportFeature)
        this.checkImagesColorRL()

      this.$nextTick(() => {
        this.initializeForm();
      })
    })
  },
  methods: {
    async initializeForm() {
      await this.$nextTick();

      const currentState = this.reportFormState;

      const rawState = toRaw(currentState);

      this.initialForm = _.cloneDeep(rawState);
      this.hasFormChanged = false;
    },

    onInit(detail) {
      this.lightGallery = detail.instance;
    },

    loadImages() {
      this.$nextTick(() => {
        this.selectedImagesLoaded.forEach(o => {
          const element = document.getElementById(o);
          if (element)
            element.click()
        });
      });
    },

    setHedisReportData(data) {
      this.hedisReportData = data;
    },

    async loadReport(reportID, unifiedFeature = this.hasUnifiedReportFeature) {
      this.selectedImagesLoaded = [];
      this.loading = true;
      NProgress.start();

      try {
        const dateFormat = this.$i18n.t('dateFormat');
        let params = { id: reportID };
        let response = await this.$reportService.getReport(params);

        this.currentReport = response.report;
        this.patient = response.patient;

        this.DateOfScreening = this.currentReport.date ? moment.utc(this.currentReport.date).format(dateFormat) : '-';
        this.dateOfBirth = this.patient.birthday ? moment.utc(this.patient.birthday).format(dateFormat) : '-';

        if (this.currentReport.images.length > 0) {
          this.selectedImagesLoaded = this.currentReport.images.split(',');
        }

        if (unifiedFeature) {
          this.currentReport.eyeAlterationAdded = this.mapEyeAlterationsToUnified(this.currentReport.patientDiagnosis);
          this.currentReport.selectedDiagnosis = this.mapDiagnosisReferences(this.currentReport.diagnosisReferences);

          this.tabActive = 'unifiedReport';
          if (this.currentReport.hedisData) {
            this.setHedisData(this.currentReport)
          }

          this.isHedisUnifiedReport = this.currentReport.isHedisReport;
        } else if (this.currentReport.isQuickReport) {
          this.tabActive = 'quickReport';
          this.loadedDiagnosisEntries = { right: [], left: [] };
          this.currentReport.patientDiagnosis.forEach((o) => {
            this.loadedDiagnosisEntries[o.laterality].push(o.key);
          });
        } else if (this.currentReport.isHedisReport) {
          this.tabActive = 'hedisReport';
          if (this.currentReport.hedisData) {
            this.setHedisData(this.currentReport)
          }
        } else {
          this.tabActive = 'completeReport';
          this.currentReport.diagnosisReferences.forEach(o => {
            this.addDiagnosisEntry(o.type.uuid, `${o.type.key} - ${o.type.description}`);
          });
          this.setCustomTemplate(this.currentReport.customTemplate);
        }


        this.$nextTick(() => {
          if (this.selectedImagesLoaded.length > 0)
            this.loadImages();
          else if (unifiedFeature)
            this.checkUnifiedDefaultImages();
          else if (this.hasHedisReportFeature)
            this.checkImagesColorRL();
        });
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },

    getSelectedDiagnosis() {
      let result = [];
      $("#selectedDiagnosis span.tag2").each(function() {
        result.push($(this).data('value'));
      });

      return result
    },

    addDiagnosisEntry(id, text) {
      if (!this.selectedDiagnosis.find(d => d.id === id))
        this.selectedDiagnosis.push({ id, text });
    },

    removeDiagnosisEntry(id) {
      this.selectedDiagnosis = this.selectedDiagnosis.filter(d => d.id !== id);

      if (this.selectedDiagnosis.length === 0) {
        this.showSelectedDiagnosisBox = false;
      }
    },

    async fetchData() {
      let params = {examID: this.exam_id}

      this.loading = true
      NProgress.start()
      try {
        let response = await this.$reportService.reportViewData(params)

        // this.examImages = response.images
        this.examImages = response.images.filter(o =>
          o.type &&
          !o.type.endsWith('VIDEO') &&
          !o.type.startsWith('OTHER_') &&
          o.type !== 'RNFL_RAW' &&
          o.type !== 'RNFL_MASKED'
        );

        this.thumbDataPath = `${response.thumbDataPath}`
        this.dataPath = `${response.dataPath}`

        if (!IS_PRODUCTION) {
          this.thumbDataPath = `http://localhost:1337${response.thumbDataPath}`
          this.dataPath = `http://localhost:1337${response.dataPath}`
        }

        this.templateOptions = [{
          id: null,
          name: this.$i18n.t('exam.selectTemplate'),
          content: ""
        }];

        response.templates.forEach(o => {
          this.templateOptions.push(o)
        })

        if (response.images.length == 0) {
          this.$message.popup(this, 'request_empty', 'operationWarning', 'warn')
        }
      } catch(err) {
        console.log('error: ', err)
        //this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger');
      }
      NProgress.done(true)
      this.loading = false
    },

    async doCreate() {
      let newReport;

      if (this.hasUnifiedReportFeature && !this.isHedisUnifiedReport) {
        await this.$refs.reportUnified.doSave();
        return;
      } else {
        newReport = Object.assign({}, this.currentReport);
        newReport['exam'] = this.exam_id;
        newReport['selectedImages'] = this.selectedImages.filter(item => item !== null);
        newReport['customTemplate'] = this.customTemplate;

        if (this.examImages.length > 0 && this.selectedImages.length === 0) {
          const ok = await this.$refs.offcanvasReportGeneric.show({
            title: this.$i18n.t('confirm'),
            text: this.$i18n.t('report.imageReportConfirmation'),
            okButton: this.$i18n.t('yes'),
            okButtonClass: 'btn-danger',
            cancelButton: this.$i18n.t('no'),
            cancelButtonClass: 'btn-outline-secondary'
          });
          if (!ok) {
            return;
          }
        }

        if (this.tabActive === 'quickReport') {
          newReport.isQuickReport = true;
          newReport.isHedisReport = false;
          newReport.quickReportEntries = this.quickReportEntries;
        } else if (this.tabActive === 'hedisReport' || this.isHedisUnifiedReport) {
          newReport.isQuickReport = false;
          newReport.isHedisReport = true;
          newReport.selectedDiagnosis = '';
          newReport.hedisData = this.hedisReportData;
        } else {
          newReport.isQuickReport = false;
          newReport.isHedisReport = false;
          newReport.selectedDiagnosis = this.getSelectedDiagnosis();
        }

        if (this.currentReport.id) {
          newReport.id = this.currentReport.id;
        }
      }

      this.loading = true;
      NProgress.start();
      try {
        let response = await this.$reportService.saveReport(newReport);

        if (this.unifiedExamType === 'noQuality') {
          await this.setAlteration();
        }

        if (this.currentReport.id) {
          this.$message.popup(this, 'general.updatedSuccess', 'operationSuccess', 'success');
        } else {
          this.$message.popup(this, 'general.createdSuccess', 'operationSuccess', 'success');
          this.loadReport(response.result.id);
          this.$nextTick(() => {
            this.fetchData();
          });
        }

        this.initializeForm();
      } catch (err) {
        this.$message.popup(this, 'general.createdError', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },

    async setAlteration() {
      let params = {
        examId: this.exam_id,
        type: 'altered'
      }

      this.loading = true
      NProgress.start()
      try {
        let response = await this.$examService.updateUnifiedExam(params)
      } catch (e) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
        this.loading = false
      }
    },

    goBack() {
      let data
      if (this.$route.query.data) {
        data = this.$route.query.data
      }

      this.$router.push({name: 'exam-show', params: {examID: this.exam_id}, query: {tab: 'report', data}})
    },

    doCancel() {
      this.$emit('cancel')
    },

    setQuickReportData(data) {
      this.quickReportEntries = data.selectedEntries;
      // this.currentReport.observation = data.observation
      // this.currentReport.observation = data.observation;
    },

    switchTemplate() {
      if (this.selectedTemplate == null) {
        this.customTemplate = ""
        this.$refs.reportForm.reloadEditorContent(this.customTemplate)
        return
      }

      for (let t of this.templateOptions) {
        if (t.id == this.selectedTemplate) {
          this.customTemplate = t.content
          this.currentReport.columns = t.columnsDefault
          break
        }
      }

      this.$refs.reportForm.reloadEditorContent(this.customTemplate)
    },

    setCustomTemplate(value) {
      this.customTemplate = value;
    },
    // doCollapse() {
    //   if (window.screen.width > 767)
    //     $('#btnCollapseMenu').click()
    //     // this.$root.$emit('collapsemenu')
    // },

    checkAll(type) {
      let checks = document.querySelectorAll('.thumbnail-box .checkbox .base-rounded-checkbox');

      for (let check of checks) {
        if (!check.classList.contains('checked')) {
          check.click();
          this.selectedImages.push(check.getAttribute('value'));
        }
      }
		},

    uncheckAll() {
      let checks = document.querySelectorAll('.thumbnail-box .checkbox .base-rounded-checkbox.checked')

      for (let check of checks) {
        check.click();
      }
      this.selectedImages = new Array();
    },

    doCheck(value) {
      let hasImg = _.find(this.selectedImages, function(o) { return o == value.uuid; });

      if (hasImg) {
        this.selectedImages = _.remove(this.selectedImages, function(n) {
          // document.getElementById(value.uuid).classList.remove('checked')
          return n != value.uuid;
        });
      } else {
        // document.getElementById(value.uuid).classList.add('checked')
        this.selectedImages.push(value.uuid);
      }
    },

    extraCustomization() {
      let self = this

      $(".lg-download").off("click").on("click", function (event) {
        event.preventDefault();
        let examDataUUID = this.href.split('/').pop()
        let item = { uuid: examDataUUID }
        self.doDownload(item)
      })

      $('.lg-fullscreen.lg-icon').click();
    },

    async doDownload(item) {
      this.loading = true
      NProgress.start()
      try {
        let params = {}
        if (item.id)
          params['id'] = item.id

        if (item.uuid)
          params['uuid'] = item.uuid

        const url = this.$examDataService.getDownloadFileUrl(params)
        this.$utils.triggerFileDownload(url)
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },

    doDownloadPdf() {
      let params = {
        id: this.currentReport.id
      }

      // try {
        this.$reportService.generateReport(params);
      // } catch (err) {
      //   this.$message.popup(this, 'request_error', 'operationFail', 'error')
      // }
    },

    getAndSetWidth() {
      let element = document.querySelectorAll('.diagnosis-gallery .thumbnail-box');
      if (element.length) {
        let cw = element[0].offsetWidth;
        element.forEach(o => {
          o.style.height = cw + 'px';
        });
      }
    },

    removeLoading(event) {
      let parentNode = event.target.parentElement.classList;
      let currentlyNode = event.target.classList;

      this.$nextTick(() => {
        currentlyNode.add('loaded')
        parentNode.remove('loading-eye')
        // this.getAndSetWidth();
      });
    },


    initializeView() {
      let vm = this;
      $('#diagnosis_select').select2({
        "language": {
          errorLoading: () => {
            return this.$i18n.t('request_error');
          },
          inputTooShort: () => {
            return this.$i18n.t('cid.inputTooShort');
          },
          noResults: () => {
            return this.$i18n.t('request_empty');
          },
          searching: () => {
            return this.$i18n.t('cid.searching');
          },
        },
        minimumInputLength: 1,
        ajax: {
          url: BASE_URL + '/api/v2/eyercloud/report/cid10-search',
          xhrFields: {
            withCredentials: true
          },
          data (params) {
            if (!params.term) {
              return;
            }

            let query = {
              search: params.term
            }
            return query;
          },
        }
      });

      $('#diagnosis_select').on('select2:select', function (e) {
        let data = e.params.data;

        if (!vm.selectedDiagnosis.find(d => d.id === data.id))
          vm.addDiagnosisEntry(data.id, data.text);

        $(this).val('').trigger("change");
      });

      $(document).on('click', '.selected-diagnosis-box .tag2 a', function(e) {
        e.preventDefault();
        const id = $(this).parent().data('value');
        vm.removeDiagnosisEntry(id);
      });
    },

    formatDate(date) {
      return moment(date).format(this.$i18n.t('dateFormat'))
    },

    resetFormToDefaults() {
      this.currentReport.columns = 2;
      this.selectedTemplate = null;
      this.customTemplate = "";
      this.hedisReportData = _.cloneDeep(DEFAULT_HEDIS_REPORT_DATA);
      this.loadedDiagnosisEntries = { right: [], left: [] };
      this.quickReportEntries = [];
      this.selectedDiagnosis = [];
      $('#selectedDiagnosis').empty();
    },

    restoreTabState(targetTab) {
      if (!this.currentReport || !this.currentReport.id) return;

      if (targetTab === 'hedisReport' && this.currentReport.isHedisReport && this.currentReport.hedisData) {
        this.setHedisData(this.currentReport);
      } else if (targetTab === 'quickReport' && this.currentReport.isQuickReport && this.currentReport.patientDiagnosis) {
        this.currentReport.patientDiagnosis.forEach((o) => {
          this.loadedDiagnosisEntries[o.laterality].push(o.key);
        });
      } else if (targetTab === 'completeReport' && !this.currentReport.isQuickReport && !this.currentReport.isHedisReport) {
        if (this.currentReport.diagnosisReferences) {
          this.currentReport.diagnosisReferences.forEach(o => {
            this.addDiagnosisEntry(o.type.uuid, `${o.type.key} - ${o.type.description}`);
          });
        }
        this.setCustomTemplate(this.currentReport.customTemplate || "");
      }
    },

    switchReportTab(newTab) {
      this.tabActive = newTab;

      this.resetFormToDefaults();
      this.restoreTabState(newTab);

      if (newTab === 'quickReport')
        this.$nextTick(() => this.$refs.reportQuick?.entriesChanged())

      if (newTab === 'hedisReport' && this.examImages.length > 0) {
        this.checkImagesColorRL();
      }
    },

    checkImagesColorRL() {
      if (this.tabActive === 'hedisReport') {
        this.uncheckAll();

        // this.$nextTick(() => {
          const imageColorR = this.examImages.find(img => img.type === "COLOR" && img.imageLaterality === "R");
          const imageColorL = this.examImages.find(img => img.type === "COLOR" && img.imageLaterality === "L");

          if (imageColorR) {
            const elementR = document.getElementById(imageColorR.uuid);
            if (elementR) {
              elementR.click();
            }
          }

          if (imageColorL) {
            const elementL = document.getElementById(imageColorL.uuid);
            if (elementL) {
              elementL.click();
            }
          }
        // })
      }
    },

    async checkUnifiedDefaultImages() {
      this.uncheckAll();

      const typesToSelect = ['COLOR', 'REDFREE', 'IA'];

      this.$nextTick(() => {
        this.examImages.forEach(image => {
          if (typesToSelect.includes(image.type)) {
            const checkboxElement = document.getElementById(image.uuid);
            if (checkboxElement)
              checkboxElement.click()
          }
        });
      });
    },

    async initializeReports() {
      this.canSwitchReportExperience = this.$utils.canSwitchReportExperience(this.session.features)
      this.hasHedisReportFeature = this.session.features.includes('hedisReport')
      const newUnifiedFeature = this.$utils.isUnifiedReportExperience(this.session.features)

      if (this.hasHedisReportFeature && !newUnifiedFeature) {
        this.tabActive = 'hedisReport';
        this.currentReport.columns = 2;
      } else {
        this.hedisReportData = { ...DEFAULT_HEDIS_REPORT_DATA };
      }

      const reportId = this.report_id || this.currentReport?.id
      if (reportId) {
        $('#selectedDiagnosis').empty();
        await this.loadReport(reportId, newUnifiedFeature)
      } else {
        this.hedisReportData = { ...DEFAULT_HEDIS_REPORT_DATA };
        $('#selectedDiagnosis').empty();
      }

      this.hasUnifiedReportFeature = newUnifiedFeature
    },

    mapPatientDiagnosisToQuickReport(patientDiagnosis) {
      const quickReportEntries = { right: [], left: [] };
      if (patientDiagnosis) {
        patientDiagnosis.forEach(item => {
          const laterality = item.laterality ? item.laterality.toLowerCase() : null;
          const key = item.key;
          if (laterality === 'right') {
            quickReportEntries.right.push(key);
          } else if (laterality === 'left') {
            quickReportEntries.left.push(key);
          }
        });
      }
      return quickReportEntries;
    },

    mapDiagnosisReferences(diagnosisReferences) {
      const selectedDiagnosis = [];
      if (diagnosisReferences) {
        diagnosisReferences.forEach(item => {
          selectedDiagnosis.push({
            id: item.type.uuid,
            text: `${item.type.key} - ${item.type.description}`
          });
        });
      }
      return selectedDiagnosis;
    },

    mapEyeAlterationsToUnified(patientDiagnosis) {
      const eyeAlterationAdded = [];
      const uniqueAlterationsLoaded = new Set();
      let nextId = 1;
      if (patientDiagnosis) {
        patientDiagnosis.forEach(item => {
          const laterality = item.laterality ? item.laterality.toLowerCase() : null;
          const key = item.key;
          let normalizedLaterality = null;
          if (laterality === 'right') {
            normalizedLaterality = 'OD';
          } else if (laterality === 'left') {
            normalizedLaterality = 'OS';
          }
          if (normalizedLaterality && key) {
            const uniqueKey = `${normalizedLaterality}_${key}`;
            if (!uniqueAlterationsLoaded.has(uniqueKey)) {
              eyeAlterationAdded.push({
                id: nextId++,
                selectedEye: normalizedLaterality,
                selectedAlteration: key
              });
              uniqueAlterationsLoaded.add(uniqueKey);
            }
          }
        });
      }
      return eyeAlterationAdded;
    },

    async unifiedReportSaved(reportID) {
      const isUpdate = !!this.currentReport.id

      this.currentReport.id = reportID

      this.currentReport.eyeAlterationAdded = []
      this.currentReport.selectedDiagnosis = []

      await this.loadReport(reportID)

      if (!isUpdate) {
        await this.fetchData()
      }

      this.$nextTick(() => {
        if (this.selectedImagesLoaded.length > 0)
          this.loadImages();

        this.initializeForm();
      })
    },

    clearUnifiedHedis() {
      if (this.currentReport) {
        this.currentReport.eyeAlterationAdded = []
        this.currentReport.selectedDiagnosis = []
        this.currentReport.customTemplate = ''
        this.currentReport.columns = 2
        this.hedisReportData = { ...DEFAULT_HEDIS_REPORT_DATA }
      }

      this.$nextTick(() => {
        this.initializeForm();
      });
    },

    async updateUnifiedReportFeature(value) {
      let params = {
        clinicID: this.session.currentClinic.id,
        enabled: value
      }

      this.loading = true
      NProgress.start()
      try {
        const response = await this.$clinicService.updateUnifiedReportFlag(params)

        await this.$authService.refreshSession(this)

        $('#selectedDiagnosis').empty();
        await this.initializeReports()
        await this.fetchData();
        await this.initializeView();
        this.isLoaded = true

        if (this.currentReport.id) {
          if (this.selectedImagesLoaded.length > 0)
            this.loadImages()
        } else
          this.hasUnifiedReportFeature ? this.checkUnifiedDefaultImages() : this.uncheckAll()

        this.$nextTick(() => {
          this.initializeForm();
        })
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
        this.loading = false
      }
    },

    setHedisData(currentReport) {
      this.hedisReportData = {
        imageQuality: currentReport.hedisData.imageQuality || { OD: 'Yes', OS: 'Yes' },
        selectedDiabetesType: currentReport.hedisData.selectedDiabetesType || 'type_2',
        eyeConditions: currentReport.hedisData.eyeConditions || { OD: '', OS: '' },
        selectedCptCodes: currentReport.hedisData.selectedCptCodes || [],
        selectedProviderComments: currentReport.hedisData.selectedProviderComments || [],
        selectedReferralOptions: currentReport.hedisData.selectedReferralOptions || []
      };
    },

    updateUnifiedData(data) {
      // initialForm snapshots unifiedReportData as null (nothing has touched the unified form
      // yet at that point - see initializeForm/reportFormState below). ReportUnified.vue's own
      // localFormData never goes back to null once touched, even after every alteration/CID10/
      // free-text entry is removed again - it just returns to an empty-but-populated object.
      // Comparing that object against the null baseline in hasFormChanged's deep-equal check
      // would then never match again, permanently stuck on "changed" - leaving the
      // Create/Update button visible even once the form is genuinely empty again. Collapse
      // back to null here whenever the emitted data has no real content, so the comparison
      // can correctly match the baseline again.
      const hasFreeText = !!(data.customTemplate || '').replace(/<[^>]*>/g, '').trim()
      const hasCID10 = (data.selectedDiagnosis || []).length > 0
      const hasQuickFindings = (data.eyeAlterationAdded || []).length > 0
      this.unifiedReportData = (hasFreeText || hasCID10 || hasQuickFindings) ? data : null;
    },
  },
  beforeUnmount() {
    // window.removeEventListener("resize", this.getAndSetWidth);
    // this.doCollapse();
  }
}
